import { Head, Link, usePage } from "@inertiajs/react"
import Navbar from "@/components/chalky/Navbar"
import Footer from "@/components/chalky/Footer"

interface Feature {
  key: string
  title: string
  description: string
}

interface ShowProps {
  meta: { title: string; description: string }
  hero: { eyebrow: string; title: string; subtitle: string }
  intro: string
  features: Feature[]
  account_cta: {
    title: string
    subtitle: string
    sign_up: string
    sign_in: string
  }
  paths: { sign_up: string; sign_in: string; dashboard: string }
}

interface PageProps {
  chrono_finish_account?: { id: number; email_address: string } | null
  [key: string]: unknown
}

const FEATURE_ICONS: Record<string, string> = {
  timing: "fa-solid fa-stopwatch",
  offline: "fa-solid fa-plug-circle-xmark",
  live: "fa-solid fa-cloud-arrow-up",
  spectator: "fa-solid fa-tv",
}

export default function Show({ meta, hero, intro, features, account_cta, paths }: ShowProps) {
  const { props } = usePage<PageProps>()
  const account = props.chrono_finish_account ?? null

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
      </Head>

      <div className="bg-[#0a0a0f] min-h-screen text-slate-300">
        <Navbar />

        {/* Hero */}
        <section className="pt-32 pb-16 px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <p className="text-sm font-medium uppercase tracking-widest text-violet-400/80 mb-4">
              {hero.eyebrow}
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">{hero.title}</h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">{hero.subtitle}</p>
          </div>
        </section>

        {/* Intro */}
        <section className="px-6">
          <div className="max-w-3xl mx-auto">
            <p className="text-base leading-relaxed text-slate-400 text-center">{intro}</p>
          </div>
        </section>

        {/* Features */}
        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature) => (
              <div
                key={feature.key}
                className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-6"
              >
                <i
                  className={`${FEATURE_ICONS[feature.key] ?? "fa-solid fa-circle-check"} text-violet-400 text-xl mb-4`}
                  aria-hidden="true"
                />
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Account CTA */}
        <section className="px-6 pb-24">
          <div className="max-w-3xl mx-auto text-center bg-white/[0.03] border border-white/[0.07] p-10 rounded-2xl">
            <h2 className="text-2xl font-bold text-white mb-3">{account_cta.title}</h2>
            <p className="text-slate-400 mb-8">{account_cta.subtitle}</p>

            {account ? (
              <Link
                href={paths.dashboard}
                className="inline-block px-8 py-3 bg-violet-600 text-white font-semibold rounded-full hover:bg-violet-500 transition-colors"
              >
                {account.email_address}
              </Link>
            ) : (
              <div className="flex gap-4 justify-center flex-wrap">
                <Link
                  href={paths.sign_up}
                  className="px-8 py-3 bg-violet-600 text-white font-semibold rounded-full hover:bg-violet-500 transition-colors"
                >
                  {account_cta.sign_up}
                </Link>
                <Link
                  href={paths.sign_in}
                  className="px-8 py-3 text-slate-300 font-semibold rounded-full border border-white/10 hover:bg-white/5 transition-colors"
                >
                  {account_cta.sign_in}
                </Link>
              </div>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  )
}
